import React from 'react';
import { Box, Typography, LinearProgress, Chip, Button } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';

const STATUS_META = {
  pending: { label: 'Queued…', color: 'default' },
  processing: { label: 'Importing…', color: 'primary' },
  success: { label: 'Completed', color: 'success' },
  failed: { label: 'Completed with errors', color: 'warning' },
  error: { label: 'Failed', color: 'error' },
};

const formatCount = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '0');

export const downloadErrorLog = (errorLog) => {
  const blob = new Blob([errorLog || ''], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'royalty-import-errors.txt';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function ImportProgress({ status, fileName, message, error }) {
  const processingStatus = (status && status.processing_status) || 'pending';
  const meta = error ? STATUS_META.error : (STATUS_META[processingStatus] || STATUS_META.pending);
  const totalRows = Number(status && status.total_rows) || 0;

  // progress_percent is approximate while running but always reads 100 on success
  const percent = processingStatus === 'success'
    ? 100
    : Math.min(100, Math.max(0, Number(status && status.progress_percent) || 0));

  // no row count yet (still queued) -> we cannot show a meaningful percentage
  const indeterminate = processingStatus === 'pending' && !totalRows;

  const errorLog = (status && status.error_log) || '';

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <UploadFileIcon fontSize="small" color="primary" />
        <Typography
          variant="subtitle2"
          sx={{ flexGrow: 1, overflowWrap: 'anywhere' }}
        >
          {fileName || 'Royalty file'}
        </Typography>
        <Chip size="small" label={meta.label} color={meta.color} />
      </Box>

      {error ? (
        <Typography variant="body2" color="error.main">
          {error}
        </Typography>
      ) : (
        <>
          <LinearProgress
            variant={indeterminate ? 'indeterminate' : 'determinate'}
            value={percent}
            color={processingStatus === 'failed' ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {processingStatus === 'pending' && !totalRows
                ? 'Waiting for a worker to pick up the file…'
                : `${formatCount(status && status.rows_processed)} / ${formatCount(totalRows)} rows`}
            </Typography>
            {!indeterminate && (
              <Typography variant="caption" color="text.secondary">
                {percent.toFixed(1)}%
              </Typography>
            )}
          </Box>
        </>
      )}

      {!error && processingStatus === 'success' && (
        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
          {`Imported ${formatCount(status && status.rows_created)} rows.`}
        </Typography>
      )}

      {!error && processingStatus === 'failed' && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="warning.main">
            {`Finished with errors. ${formatCount(status && status.rows_created)} rows were still imported — review the rows below.`}
          </Typography>
          {errorLog && (
            <>
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  maxHeight: 150,
                  overflow: 'auto',
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                }}
              >
                {errorLog}
              </Box>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => downloadErrorLog(errorLog)}
                sx={{ mt: 1 }}
              >
                Download error log
              </Button>
            </>
          )}
        </Box>
      )}

      {!error && message && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {message}
        </Typography>
      )}
    </Box>
  );
}
